import { Component, h } from 'preact'
import Button from '../ui-components/Button'
import ColorPicker from './ColorPicker'
import { StylingConfigType } from './configurator'
import InputGroup from '../ui-components/InputGroup'

export type ColorsTheme = {
  title: string,
  key: string,
  description:string,
  checkbox?: {
    key: string,
    title: string
  }
}

const COLORS: Array<ColorsTheme> = [
  {
    title: 'Branded color',
    key: 'config--main-color-brand',
    description: 'Define the brand color for the most common interaction elements and visual cues such as: buttons, URLs, labels.'
  },
  {
    title: 'Secondary color',
    key: 'config--main-color-brand-secondary',
    description: 'Define the secondary color for secondary buttons and actions.',
  },
  {
    title: 'Heading color',
    key: 'config-header-color',
    description: 'Defined the heading color for headers and titles.'
  },
  {
    title: 'Text color',
    key: 'config--main-color-night',
    description: 'Define the text color for paragraphs'
  },
  {
    title: 'Metadata color',
    key: 'config-meta-text-color',
    description: 'Define the metadata color for topic information and post actions.'
  },
  {
    title: 'Placeholder color',
    key: 'config-main-navigation-search-placeholder-color',
    description: 'Define the placeholder color for input field placeholders.'
  },
  {
    title: 'Page background color',
    key: 'config-body-background-color',
    description: 'Define the background color for your community.',
  },
  {
    title: 'Border color',
    key: 'config--main-border-base-color',
    description: 'Define the border color for dividers, cards and containers.'
  },
  {
    title: 'Success color',
    key: 'config--main-color-success',
    description: 'Define the success color for best answer label, icon and success notifications.'
  },
  {
    title: 'Alert color',
    key: 'config--main-color-alert',
    description: 'Define the alert color for cancel and delete actions and error notifications.'
  },
  {
    title: 'Info color',
    key: 'config--main-color-info',
    description: 'Define the info color for information messages and notifications.'
  },
  {
    title: 'Question color',
    key: 'config-topic-question-color',
    description: 'Define the question color for question labels and icons.'
  },
  {
    title: 'Disabled color',
    key: 'config--main-color-disabled',
    description: 'Define the disabled color for inactive and disabled elements.'
  },
  {
    title: 'Featured topic background color',
    key: 'config-notification-widget-background-color',
    description: 'Define the background color for the featured topic in the banner (featured_banner) and sidebar (featured_side).',
    checkbox: {
      title: 'Use Brand color',
      key: 'config--main-color-brand'
    }
  },
  {
    title: 'Featured topic text color',
    key: 'config-notification-widget-color',
    description: 'Define the text color for the featured topic in the banner (featured_banner) and sidebar (featured_side).'
  },
  {
    title: 'Introduction widget background color',
    key: 'config-widget-cta-background-color',
    description: 'Define the background color for the introduction widget on the community homepage.',
    checkbox: {
      title: 'Use Background color',
      key: 'config-body-background-color'
    }
  },
  {
    title: 'Introduction widget text color',
    key: 'config-widget-cta-color',
    description: 'Define the text color for the introduction widget on the community homepage.',
    checkbox: {
      title: 'Use Text color',
      key: 'config--main-color-night'
    }
  },
  {
    title: 'Homepage heading color',
    key: 'config-hero-color',
    description: 'Define the text color for the heading (displayed on the hero image) on the community homepage.'
  },
  {
    title: 'Category heading and description color',
    key: 'config-subcategory-hero-color',
    description: 'Define the text color for the heading and description (displayed on the hero image) on the category pages.'
  }
]

interface Props {
  updatedStyling: StylingConfigType,
  defaultStyling: StylingConfigType,
  onUpdate: (updatedStyling: StylingConfigType) => void
}

interface State {
  enabledColorSelector: null | string
}

export default class Colors extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      enabledColorSelector: null
    }

    this.updateColors = this.updateColors.bind(this)
    this.resetColors = this.resetColors.bind(this)
  }

  updateColors (key: string, color: string | null) {
    this.props.onUpdate({ ...this.props.updatedStyling, ...{ [key]: color } })
  }

  resetColors () {
    this.props.onUpdate(this.props.defaultStyling)
  }

  render ({ updatedStyling, defaultStyling }: Props, { enabledColorSelector }: State) {
    return (
      <div className='box__pad'>
        <ul className='theme-configuration_colors-list'>
          {
            COLORS.map(color => (
              <li className={`theme-configuration_colors-list-item ${color.title.toLocaleLowerCase().split(' ').join('-')}`}>
                <strong>{ color.title }</strong>
                <p>{ color.description }</p>
                <ColorPicker
                  color={!!color.checkbox && updatedStyling[color.key] === null ? updatedStyling[color.checkbox.key] : updatedStyling[color.key]}
                  disabled={!!color.checkbox && updatedStyling[color.key] === null}
                  colorKey={color.key}
                  disableColorSelector={() => this.setState({ enabledColorSelector: null })}
                  setColorSelector={() => this.setState({ enabledColorSelector: color.key })}
                  updateColor={(newColor: string) => this.updateColors(color.key, newColor)}
                  isColorSelectorEnabled={enabledColorSelector === color.key}
                />
                {
                  color.checkbox ? (
                    <div className='use-default'>
                      <InputGroup
                        title={color.checkbox.title}
                        type='checkbox'
                        checked={updatedStyling[color.key] === null ? 'checked' : ''}
                        onChangeCheckbox={(e: {target: HTMLInputElement}) => {
                          this.updateColors(color.key, e.target.checked ? null : color.checkbox ? updatedStyling[color.checkbox.key] : null)
                        }}
                      />
                    </div>
                  ) : null
                }
              </li>
            ))
          }
          <Button warning onClick={this.resetColors}>
            Reset to defaults
          </Button>
        </ul>
      </div>
    )
  }
}